import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { hasAnyRole } from "../auth/authUi";
import { CMMS_FIELD_ROLES } from "../features/cmms/cmmsRoles";
import { useIsCmmsEnabled } from "../features/cmms/cmmsFeature";
import { fetchScheduleResetLog } from "../fetchers/cmmsRecurringSchedule";
import { useLocalStorage } from "./useLocalStorage";

/*
 * What has reset one schedule's counter, newest first.
 *
 * Shaped after useToolServiceHistory, including both of its gates: the module switch so an OEE
 * install without CMMS never asks, and the role check because the endpoint is Cmms_Field. The
 * schedules route admits [1,2,3,4] and the reset itself is Cmms_Manager — reading the log is
 * deliberately wider than writing to it, since the technician who performs the service is the one
 * who most needs to know when it was last done.
 *
 * `scheduleId` gates the request rather than defaulting to anything. There is no company-wide form
 * of this read, so a null schedule means "ask nothing", not "ask about everything".
 *
 * On a tool schedule the answer is the *tool's* history, work-order closes included — a service
 * satisfies every schedule watching that tool, so there is no narrower honest answer. The drawer
 * says so on screen; without that, entries nobody in this view created read as a bug.
 */
export function useScheduleResetLog(scheduleId, { take } = {}) {
  const { roleIds } = useAuth();
  const [companyKey] = useLocalStorage("companyKey", 1);
  const cmmsEnabled = useIsCmmsEnabled();

  const isCmmsUser = hasAnyRole(roleIds, CMMS_FIELD_ROLES);

  const query = useQuery(
    ["cmmsScheduleResetLog", companyKey, scheduleId ?? null, take ?? null],
    () => fetchScheduleResetLog(companyKey, scheduleId, take),
    {
      enabled: Boolean(scheduleId) && cmmsEnabled && isCmmsUser && Boolean(companyKey),
      // The log changes when someone resets or when the nightly sweep raises an order, neither of
      // which happens while a drawer is open. Same minute as the readings beside it.
      staleTime: 60000,
    },
  );

  return {
    entries: query.data ?? [],
    // isInitialLoading, not isLoading: a disabled query stays status:"loading" forever in
    // react-query v4, and this one is disabled every moment no schedule is selected — which is
    // most of them. isLoading would leave the panel showing a skeleton for a closed drawer.
    isLoading: query.isInitialLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

export default useScheduleResetLog;
